import React from 'react';
import type { CsatReportSummary } from '@troia-v3/types';

import { DashCard, MetricTile, formatNumber, formatPct } from '@/shared/components/ui/dashboard';

const formatAverage = (value: number): string =>
  new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value);

interface CsatSummaryPanelProps {
  summary: CsatReportSummary;
}

/**
 * Resumo do recorte no topo do relatório. Situação e nota não mudam estes
 * números (o servidor as ignora) — senão a taxa de "só respondidas" daria 100%.
 */
export const CsatSummaryPanel: React.FC<CsatSummaryPanelProps> = ({ summary }) => {
  const answered = summary.answered;

  return (
    <div className="space-y-3" data-testid="csat-summary">
      <div className="grid grid-cols-2 min-[900px]:grid-cols-4 gap-3">
        <MetricTile
          icon="send"
          label="Pesquisas enviadas"
          value={formatNumber(summary.sent)}
          caption={`${formatNumber(summary.awaiting)} aguardando resposta`}
          testId="csat-summary-sent"
        />
        <MetricTile
          icon="check-circle"
          label="Taxa de resposta"
          value={summary.responseRate === null ? '—' : formatPct(summary.responseRate)}
          caption={`${formatNumber(answered)} ${answered === 1 ? 'respondida' : 'respondidas'}`}
          testId="csat-summary-response-rate"
        />
        <MetricTile
          icon="star"
          label="Nota média"
          value={summary.averageRating === null ? '—' : formatAverage(summary.averageRating)}
          caption="1 é a melhor nota, 5 a pior"
          testId="csat-summary-average"
        />
        <MetricTile
          icon="clock"
          label="Sem resposta"
          value={formatNumber(summary.noAnswer)}
          caption={`${formatNumber(summary.invalid)} ${summary.invalid === 1 ? 'resposta inválida' : 'respostas inválidas'}`}
          testId="csat-summary-no-answer"
        />
      </div>

      <DashCard
        icon="bar-chart"
        title="Distribuição das notas"
        subtitle="Pesquisas respondidas, por nota — 1 é a melhor"
        testId="csat-summary-distribution"
      >
        <div className="grid grid-cols-1 min-[640px]:grid-cols-5 gap-x-4 gap-y-3">
          {summary.distribution.map(({ value, count }) => {
            const pct = answered > 0 ? (count / answered) * 100 : 0;
            return (
              <div key={value} className="flex flex-col gap-1.5 min-w-0" data-testid={`csat-summary-rating-${value}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-xs font-semibold" style={{ color: 'var(--theme-card-text)' }}>
                    Nota {value}
                  </span>
                  <span
                    className="text-xs"
                    style={{ color: 'var(--theme-muted-text)', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {formatNumber(count)} · {formatPct(pct)}
                  </span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--theme-card-border)' }}>
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${pct}%`, backgroundColor: 'var(--theme-primary)' }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </DashCard>
    </div>
  );
};

CsatSummaryPanel.displayName = 'CsatSummaryPanel';
